import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle2, AlertCircle, CalendarX } from 'lucide-react';

const REASON_LABELS = {
  holiday: { label: 'Public Holiday', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  friday_prayer: { label: 'Friday Prayer', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  court_unavailable: { label: 'Court Unavailable', color: 'text-rose-700 bg-rose-50 border-rose-200' },
  availability_check_failed: { label: 'Check Failed', color: 'text-slate-700 bg-slate-50 border-slate-200' },
};

function reasonChip(reason) {
  const cfg = REASON_LABELS[reason] || { label: reason || 'Skipped', color: 'text-slate-700 bg-slate-50 border-slate-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function SeriesPreviewModal({
  open, onOpenChange,
  loading, preview, error,
  onConfirm, submitting,
}) {
  const will = preview?.candidates?.filter((c) => c.status === 'will_book') || [];
  const skipped = preview?.candidates?.filter((c) => c.status === 'skipped') || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="series-preview-modal">
        <DialogHeader>
          <DialogTitle>Review Recurring Booking Series</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-muted-foreground">Calculating occurrences…</div>
        )}

        {error && (
          <Card className="p-4 border-rose-200 bg-rose-50 text-rose-800 text-sm" data-testid="series-preview-error">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          </Card>
        )}

        {!loading && preview && !error && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Card className="p-3 border-emerald-200 bg-emerald-50">
                <p className="text-2xl font-black text-emerald-700" data-testid="will-book-count">{preview.will_book_count}</p>
                <p className="text-[11px] uppercase text-emerald-800 font-bold">Will Book</p>
              </Card>
              <Card className="p-3 border-amber-200 bg-amber-50">
                <p className="text-2xl font-black text-amber-700" data-testid="skipped-count">{preview.skipped_count}</p>
                <p className="text-[11px] uppercase text-amber-800 font-bold">Skipped</p>
              </Card>
              <Card className="p-3 border-slate-200">
                <p className="text-2xl font-black text-slate-800" data-testid="total-est">${(preview.total_estimated || 0).toFixed(2)}</p>
                <p className="text-[11px] uppercase text-slate-700 font-bold">Estimated</p>
              </Card>
            </div>

            <p className="text-xs text-muted-foreground">
              Locked rate: <strong>${(preview.locked_rate_per_hour || 0).toFixed(2)}/hr/court</strong> — won't drift if pricing changes mid-series.
            </p>

            <div className="max-h-72 overflow-y-auto pr-1 space-y-1.5">
              {will.map((c) => (
                <div
                  key={`book-${c.date}`}
                  className="flex items-center justify-between p-2 rounded-md border border-emerald-200 bg-emerald-50/50 text-sm"
                  data-testid={`row-book-${c.date}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    {formatDate(c.date)}
                  </span>
                  <span className="text-[11px] uppercase font-bold text-emerald-800">Booking</span>
                </div>
              ))}
              {skipped.map((c) => (
                <div
                  key={`skip-${c.date}`}
                  className="flex items-center justify-between p-2 rounded-md border border-slate-200 bg-slate-50 text-sm"
                  data-testid={`row-skip-${c.date}`}
                >
                  <span className="inline-flex items-center gap-2 text-slate-600">
                    <CalendarX className="w-4 h-4" />
                    <span className="line-through">{formatDate(c.date)}</span>
                  </span>
                  {reasonChip(c.reason)}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="series-cancel">
            Back to edit
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading || submitting || !!error || !preview || preview.will_book_count === 0}
            className="btn-primary"
            data-testid="series-confirm"
          >
            {submitting ? 'Creating…' : `Confirm & Create ${preview?.will_book_count || 0} Booking${preview?.will_book_count === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
